import React, { useEffect, useMemo, useState } from 'react';
import { DatePicker, Form, Select, Spin, Typography } from 'antd';
import type { Dayjs } from 'dayjs';
import { profileService } from '../../../services/profile.service';
import type { State, City } from '../../../types/profile.types';
import { genderLabels, educationLabels } from '../../../utils/profile-labels';
import { AgeRange, AGE_RANGE_LABELS } from '../../../utils/age-range';
import { previewRecipients, type FiltersCriteria } from '../../../services/bulk-email.service';

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface UserFilterFieldsProps {
  onChange: (criteria: FiltersCriteria, recipientCount: number | null) => void;
}

/**
 * Filtro de destinatários por dados de cadastro/perfil (US1, FR-003). Todos os
 * filtros preenchidos se combinam com E (Clarifications). A contagem de
 * destinatários é recalculada via POST /admin/bulk-email/preview a cada
 * mudança (debounced), reaproveitando exatamente a mesma resolução usada no
 * disparo (research.md §5).
 */
export const UserFilterFields: React.FC<UserFilterFieldsProps> = ({ onChange }) => {
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [active, setActive] = useState<boolean | undefined>(undefined);
  const [gender, setGender] = useState<string | undefined>(undefined);
  const [ageRangeKey, setAgeRangeKey] = useState<string | undefined>(undefined);
  const [educationLevel, setEducationLevel] = useState<string | undefined>(undefined);
  const [stateIds, setStateIds] = useState<number[]>([]);
  const [cityIds, setCityIds] = useState<number[]>([]);

  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loadingCount, setLoadingCount] = useState(false);
  const [recipientCount, setRecipientCount] = useState<number | null>(null);

  useEffect(() => {
    profileService.getStates().then(setStates).catch(() => setStates([]));
  }, []);

  useEffect(() => {
    if (stateIds.length === 0) {
      setCities([]);
      setCityIds([]);
      return;
    }
    Promise.all(stateIds.map((id) => profileService.getCitiesByState(String(id))))
      .then((results) => setCities(results.flat()))
      .catch(() => setCities([]));
  }, [stateIds]);

  const criteria: FiltersCriteria = useMemo(
    () => ({
      createdFrom: dateRange?.[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
      createdTo: dateRange?.[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
      active,
      gender: gender as FiltersCriteria['gender'],
      ageRangeKey,
      educationLevel,
      stateIds: stateIds.length ? stateIds : undefined,
      cityIds: cityIds.length ? cityIds : undefined,
    }),
    [dateRange, active, gender, ageRangeKey, educationLevel, stateIds, cityIds]
  );

  useEffect(() => {
    let cancelled = false;
    setLoadingCount(true);
    const timer = setTimeout(() => {
      previewRecipients({ source: 'FILTERS', ...criteria })
        .then((result) => {
          if (cancelled) return;
          setRecipientCount(result.recipientCount);
          onChange(criteria, result.recipientCount);
        })
        .catch(() => {
          if (cancelled) return;
          setRecipientCount(null);
          onChange(criteria, null);
        })
        .finally(() => {
          if (!cancelled) setLoadingCount(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteria]);

  return (
    <Form layout="vertical">
      <Form.Item label="Data de criação (de / até)">
        <RangePicker
          style={{ width: '100%' }}
          value={dateRange as any}
          onChange={(values) => setDateRange(values as [Dayjs, Dayjs] | null)}
          format="DD/MM/YYYY"
        />
      </Form.Item>
      <Form.Item label="Situação do cadastro">
        <Select
          allowClear
          placeholder="Ativo e inativo"
          value={active}
          onChange={setActive}
          options={[
            { value: true, label: 'Ativo' },
            { value: false, label: 'Inativo' },
          ]}
        />
      </Form.Item>
      <Form.Item label="Gênero">
        <Select
          allowClear
          placeholder="Todos"
          value={gender}
          onChange={setGender}
          options={Object.entries(genderLabels).map(([value, label]) => ({ value, label }))}
        />
      </Form.Item>
      <Form.Item label="Faixa etária">
        <Select
          allowClear
          placeholder="Todas"
          value={ageRangeKey}
          onChange={setAgeRangeKey}
          options={Object.values(AgeRange).map((value) => ({
            value,
            label: AGE_RANGE_LABELS[value],
          }))}
        />
      </Form.Item>
      <Form.Item label="Escolaridade">
        <Select
          allowClear
          placeholder="Todas"
          value={educationLevel}
          onChange={setEducationLevel}
          options={Object.entries(educationLabels).map(([value, label]) => ({ value, label }))}
        />
      </Form.Item>
      <Form.Item label="Estado">
        <Select
          allowClear
          mode="multiple"
          placeholder="Todos os estados"
          value={stateIds}
          onChange={(values) => setStateIds(values)}
          options={states.map((state) => ({ value: Number(state.id), label: state.name }))}
        />
      </Form.Item>
      <Form.Item label="Cidade">
        <Select
          allowClear
          mode="multiple"
          placeholder={stateIds.length ? 'Todas as cidades dos estados selecionados' : 'Selecione um estado primeiro'}
          disabled={stateIds.length === 0}
          value={cityIds}
          onChange={(values) => setCityIds(values)}
          options={cities.map((city) => ({ value: Number(city.id), label: city.name }))}
        />
      </Form.Item>

      <Text type="secondary">
        {loadingCount ? (
          <Spin size="small" />
        ) : recipientCount !== null ? (
          `${recipientCount} destinatário(s) encontrado(s) com os filtros atuais.`
        ) : (
          'Não foi possível calcular a contagem de destinatários.'
        )}
      </Text>
    </Form>
  );
};
